package fr.arcplex.sooner.webui

import fr.arcplex.network.model.RailItem

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImagePainter
import coil3.compose.SubcomposeAsyncImage
import coil3.compose.SubcomposeAsyncImageContent
import fr.arcplex.network.logging.Logger
import fr.arcplex.network.remoteImageUrl
import fr.arcplex.sooner.ui.theme.TVMuted
import fr.arcplex.sooner.ui.theme.TVTxt

/**
 * The one item renderer behind every rail in the app (legacy menu rails and
 * the Coherence poster rails): transparent card, optional TV focus border, and the
 * focus chain. Content (image/txt branches for legacy items, poster+title for the
 * Coherence rails) is supplied through the content slot.
 */
@Composable
fun RailItemView(
    modifier: Modifier = Modifier,
    onItemClick: () -> Unit = {},
    isTV: Boolean = false,
    requester: FocusRequester? = null,
    upTarget: (() -> FocusRequester?)? = null,
    downTarget: (() -> FocusRequester?)? = null,
    onFocused: (Boolean) -> Unit = {},
    content: @Composable () -> Unit
) {
    val focusRequester = requester ?: remember { FocusRequester() }
    val isFocusedState = remember { mutableStateOf(false) }

    // Match RailItem: transparent card, 3dp white focus border on TV.
    val focusModifier = if (isTV) {
        Modifier
            .focusRequester(focusRequester)
            .onFocusChanged { state ->
                isFocusedState.value = state.isFocused || state.hasFocus
                if (state.isFocused) onFocused(true)
            }
            .then(if (isFocusedState.value) Modifier.border(3.dp, Color.White) else Modifier)
    } else Modifier

    // focusProperties must precede the focus target (Card's clickable) in the chain.
    val focusPropertiesModifier = Modifier.focusProperties {
        val up = upTarget?.invoke()
        if (up != null) this.up = up
        val down = downTarget?.invoke()
        if (down != null) this.down = down
    }

    Card(
        onClick = onItemClick,
        modifier = focusModifier.then(focusPropertiesModifier).then(modifier),
        shape = RoundedCornerShape(0.dp),
        colors = CardDefaults.cardColors(
            containerColor = Color.Transparent,
            contentColor = TVTxt,
            disabledContainerColor = Color.Transparent,
            disabledContentColor = TVTxt
        ),
        elevation = CardDefaults.cardElevation(
            defaultElevation = 0.dp,
            pressedElevation = 0.dp,
            focusedElevation = 0.dp,
            hoveredElevation = 0.dp
        )
    ) {
        content()
    }
}

/**
 * Content branches for plain [RailItem]s (image/txt). The Coherence poster rails
 * render their own content through [RailItemView]'s content slot instead.
 */
@Composable
fun RailItemContent(item: RailItem) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier.height(item.height.dp).width(item.width.dp)
    ) {
        if (item.type == "image") {
            if (item.content.isNotBlank()) {
                Box(
                    contentAlignment = Alignment.Center,
                    modifier = Modifier.background(Color.Transparent)
                ) {
                    SubcomposeAsyncImage(
                        remoteImageUrl(item.content),
                        contentDescription = null,
                        // Match RailItem poster: cover, not fit.
                        contentScale = ContentScale.Crop,
                        modifier = Modifier
                            .height(item.height.dp)
                            .width(item.width.dp)
                            // Static placeholder backdrop (non-poster): same gradient as .lc-card .th.
                            .background(
                                Brush.linearGradient(
                                    listOf(Color(0xFF3B4250), Color(0xFF171B21)),
                                    start = Offset.Zero,
                                    end = Offset.Infinite
                                )
                            )
                            .clip(RoundedCornerShape(3.dp))
                            .border(1.dp, Color(0x24FFFFFF), RoundedCornerShape(3.dp))
                    ) {
                        val state by painter.state.collectAsState()
                        Logger.d("Async State", state.toString())
                        if (state is AsyncImagePainter.State.Success) {
                            SubcomposeAsyncImageContent()
                        } else {
                            CircularProgressIndicator()
                        }
                    }
                }
            }
            if (!item.label.isNullOrEmpty()) {
                Text(
                    text = item.label.orEmpty(),
                    color = TVMuted,
                    fontSize = 9.sp,
                    textAlign = TextAlign.Center,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.fillMaxWidth().padding(top = 3.dp)
                )
            }
        } else if (item.type == "txt") {
            Text(
                text = item.content,
                color = TVTxt,
                modifier = Modifier.background(Color.Transparent)
            )
        } else {
            Logger.e("FILMO", "Item type ${item.type}")
        }
    }
}
